import { Text } from '@thanx/uikit/text'
import { locationsApi } from 'api/locations'
import { useGetRecoveryActivitiesQuery } from 'api/recoveryAi'
import FeedbackTable, { FeedbackTableRow } from 'components/FeedbackTable'
import type { SortDirection } from 'components/SortableColumnHeader'
import TablePaginationFooter, {
  DEFAULT_PAGE_SIZE,
  PageSize,
} from 'components/TablePaginationFooter'
import TableToolbar, { ToolbarLocationOption } from 'components/TableToolbar'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  hideResolvedChanged,
  selectHideResolved,
} from 'slices/recoveryAiActivity'
import { useDebouncedCallback } from 'use-debounce'

const t = buildTranslate('recovery_ai.activity')

const SEARCH_DEBOUNCE_MS = 300

// The single Escalations table (RAI redesign): the old Needs-attention/Resolved
// sub-tabs collapse into one FeedbackTable + a "hide resolved" toggle. This
// view is permanently scoped to escalated (+ resolved) cases — it never shows
// every status. Hide-resolved on → status_filter 'escalated' (escalated only);
// off → status_filter 'escalated_or_resolved' (escalated + resolved), so
// unchecking narrows nothing away from that scope, it just adds resolved cases
// back in. The location Select scopes the table to one store via the nexus
// location_id param (AI-682); the "All locations" sentinel (null) omits it.
const Activity = () => {
  const dispatch = useDispatch()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  // Feature-level state lives in the recoveryAiActivity slice (not useState):
  // Redux survives every in-SPA navigation, including the static
  // `to="/recovery_ai/escalations"` case-detail "Back to Escalations" Link,
  // which carries no query string for a URL param to ride along on.
  const hideResolved = useSelector(selectHideResolved)
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  // FeedbackTable owns the Date header's sort control; the scene holds the state
  // and feeds it back as the query's sort_direction (date is the only sortable
  // column, so nexus needs the direction alone — no `sort` field).
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Populates the location Select. The base query already scopes /locations to
  // the current merchant, so no merchant param is needed; baseui filters the
  // list as the operator types.
  const { data: locationsData } = locationsApi.useGetLocationsQuery({
    coming_soon: false,
  })
  const locationOptions = useMemo<ToolbarLocationOption[]>(
    () =>
      (locationsData?.locations ?? []).map(location => ({
        id: location.id,
        label: location.name,
      })),
    [locationsData]
  )

  const { data, isLoading, isError, refetch } = useGetRecoveryActivitiesQuery({
    page,
    per_page: pageSize,
    status_filter: hideResolved ? 'escalated' : 'escalated_or_resolved',
    search: search || undefined,
    sort_direction: sortDirection ?? undefined,
    // Grape coerces the string back to Integer; null (All locations) omits it.
    location_id:
      selectedLocationId != null ? String(selectedLocationId) : undefined,
  })

  // Keep the raw (possibly undefined) query ref as the memo dep — RTK Query
  // returns a stable array while the cache entry is unchanged, so `?? []` moves
  // inside the callback to avoid a fresh [] destabilizing the deps each render.
  const activities = data?.data

  // Retain the last known pagination meta across refetches. Each new
  // page/size/sort fetch has no cache entry yet, so `data` is briefly undefined
  // and a naive `?? 0` collapses totalCount to 0 — which unmounts the footer (a
  // focusable page input + rows-per-page select) mid-interaction and drops
  // focus. Holding the last values keeps the footer mounted through the load.
  const [lastMeta, setLastMeta] = useState({ totalPages: 1, totalCount: 0 })
  useEffect(() => {
    if (data?.meta) {
      setLastMeta({
        totalPages: data.meta.totalPages,
        totalCount: data.meta.totalCount,
      })
    }
  }, [data])
  const { totalPages, totalCount } = lastMeta

  // If the current page falls out of range after a refetch (e.g. resolving the
  // last row on a page shrinks totalPages), snap it back so the table never
  // shows an empty out-of-bounds page.
  useEffect(() => {
    if (data?.meta && page > data.meta.totalPages) {
      setPage(data.meta.totalPages || 1)
    }
  }, [data, page])

  const [debouncedSetSearch, cancelDebouncedSearch] = useDebouncedCallback(
    (value: string) => {
      setSearch(value)
      setPage(1)
    },
    SEARCH_DEBOUNCE_MS
  )

  // Drop any in-flight debounced update on unmount so we don't setState on an
  // unmounted tree.
  useEffect(() => cancelDebouncedSearch, [cancelDebouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const handleSortChange = (direction: 'asc' | 'desc') => {
    setSortDirection(direction)
    setPage(1)
  }

  const handleHideResolvedChange = (next: boolean) => {
    dispatch(hideResolvedChanged(next))
    setPage(1)
  }

  const handleLocationChange = (locationId: number | null) => {
    setSelectedLocationId(locationId)
    setPage(1)
  }

  // Paging is server-side, so a size change must reset to page 1 — otherwise a
  // current page beyond the new (larger) page's range fetches an empty set and
  // the table falsely reads "No escalations to review". Mirrors every other
  // filter transition in this scene.
  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size)
    setPage(1)
  }

  const rows = useMemo(
    () =>
      (activities ?? []).map(
        (activity): FeedbackTableRow => ({
          id: activity.id,
          rating: 'down', // every recovery case is a negative-feedback case
          date: activity.date,
          customerName: activity.customerName,
          locationName: activity.storeName,
          resolvedBy: activity.resolvedBy,
          conversationId: activity.conversationId,
          // No comment or user id on the escalations wire — this surface's View
          // link always targets the case detail, which fetches its own data.
          comment: null,
          userId: null,
          userUid: null,
        })
      ),
    [activities]
  )

  return (
    <div>
      {/* No top margin: the surrounding TabPane's own py-xl padding already
          supplies the gap below the tab bar (matches the old tabs->metrics-row
          spacing now that the metrics cards are gone). */}
      <Text.Header4 bold color="grey90" className="mb-m">
        {t('cases_heading')}
      </Text.Header4>

      <div className="mb-m">
        <TableToolbar
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          searchAriaLabel={t('search_aria')}
          hideResolved={hideResolved}
          onHideResolvedChange={handleHideResolvedChange}
          hideResolvedLabel={t('hide_resolved_label')}
          locations={locationOptions}
          selectedLocationId={selectedLocationId}
          onLocationChange={handleLocationChange}
        />
      </div>

      <FeedbackTable
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        getViewPath={row =>
          row.conversationId ? `/recovery_ai/cases/${row.conversationId}` : null
        }
        emptyMessage={t('empty.default')}
        filteredEmptyMessage={t('empty.filtered')}
        errorMessage={t('error.body')}
        // hideResolved defaults on, so the landing view is already filtered —
        // fold it in so an all-resolved merchant sees the "filtered" empty copy,
        // not the "nothing here at all" copy.
        hasActiveFilters={!!search || hideResolved}
        // Figma MX-RecoveryAI spec (2L1kCbUYlhHJqhmtNt0kpW, node 2318:64225): the
        // Escalations table's Resolved-by "td" now renders the same light
        // spearmint10 chip + trailing sparkle cluster as the Details tab, not the
        // legacy solid pill. See FeedbackTable's subtleResolvedBadge doc comment.
        subtleResolvedBadge
      />

      <TablePaginationFooter
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}

export default Activity
